/**
 * AgentActivityPanel — shows Claude Code agent activity during API testing.
 *
 * Displays a pulsing indicator when the agent is active, a collapsible tool
 * activity log, and parsed investigation findings when complete.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  SmartToy as AgentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Build as ToolIcon,
  CheckCircle as DoneIcon,
  Error as ErrorIcon,
  Lightbulb as RecommendIcon,
  BugReport as RootCauseIcon,
} from '@mui/icons-material';
import { AgentToolActivity, AgentInvestigationFindings } from '../types';

interface AgentActivityPanelProps {
  active: boolean;
  task: string | null;
  toolEvents: AgentToolActivity[];
  findings: AgentInvestigationFindings | null;
}

const AgentActivityPanel: React.FC<AgentActivityPanelProps> = ({
  active, task, toolEvents, findings,
}) => {
  const [toolsExpanded, setToolsExpanded] = useState(false);

  const taskLabel = task === 'failure_investigation'
    ? 'Investigating Failures'
    : task === 'code_generation'
      ? 'Generating & Validating Code'
      : task || 'Agent Task';

  const completedTools = toolEvents.filter((e) => e.phase === 'end');
  const hasErrors = toolEvents.some((e) => e.is_error);

  return (
    <Box sx={{
      borderTop: '1px solid', borderColor: 'divider',
      bgcolor: active ? '#f3f0ff' : findings ? '#f8f9fa' : undefined,
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.75,
        px: 1.5, py: 0.75,
      }}>
        <AgentIcon sx={{
          fontSize: '1rem',
          color: active ? 'primary.main' : 'text.secondary',
          animation: active ? 'pulse 1.5s ease-in-out infinite' : 'none',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.4 },
          },
        }} />
        <Typography variant="caption" fontWeight={700} fontSize="0.75rem"
          color={active ? 'primary.main' : 'text.secondary'}>
          {taskLabel}
        </Typography>
        {active && (
          <Chip label="Active" size="small" color="primary" variant="filled"
            sx={{ height: 16, fontSize: '0.55rem' }} />
        )}
        {!active && findings && (
          <Chip label="Complete" size="small" color="success" variant="outlined"
            sx={{ height: 16, fontSize: '0.55rem' }} />
        )}
        {toolEvents.length > 0 && (
          <Typography variant="caption" color="text.secondary" fontSize="0.6rem"
            sx={{ ml: 'auto' }}>
            {completedTools.length} tool call{completedTools.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {/* Progress bar when active */}
      {active && (
        <LinearProgress variant="indeterminate" sx={{ height: 2, mx: 1.5 }} />
      )}

      {/* Tool Activity Log (collapsible) */}
      {toolEvents.length > 0 && (
        <Box sx={{ px: 1.5 }}>
          <Box
            onClick={() => setToolsExpanded(!toolsExpanded)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              cursor: 'pointer', py: 0.25,
              '&:hover': { bgcolor: 'action.hover' },
              borderRadius: 0.5,
            }}
          >
            <ToolIcon sx={{ fontSize: '0.7rem', color: 'text.secondary' }} />
            <Typography variant="caption" fontSize="0.6rem" color="text.secondary">
              Tool activity
            </Typography>
            {toolsExpanded
              ? <ExpandLessIcon sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />
              : <ExpandMoreIcon sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />}
          </Box>

          <Collapse in={toolsExpanded}>
            <Box sx={{
              maxHeight: 200, overflow: 'auto', mb: 0.75,
              border: '1px solid', borderColor: 'divider',
              borderRadius: 0.5, bgcolor: 'background.paper',
            }}>
              {toolEvents.map((evt, i) => (
                <Box key={`${evt.tool_use_id}-${evt.phase}-${i}`} sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 0.5,
                  px: 0.75, py: 0.25,
                  borderBottom: '1px solid', borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                  bgcolor: evt.is_error ? 'error.50' : undefined,
                }}>
                  {evt.phase === 'end' ? (
                    evt.is_error
                      ? <ErrorIcon sx={{ fontSize: '0.65rem', color: 'error.main', mt: 0.25 }} />
                      : <DoneIcon sx={{ fontSize: '0.65rem', color: 'success.main', mt: 0.25 }} />
                  ) : (
                    <ToolIcon sx={{ fontSize: '0.65rem', color: 'info.main', mt: 0.25 }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={600} fontSize="0.6rem"
                      fontFamily="monospace">
                      {evt.tool}
                    </Typography>
                    {evt.phase === 'start' && evt.input && (
                      <Typography variant="caption" fontSize="0.55rem" color="text.secondary"
                        sx={{
                          display: 'block', fontFamily: 'monospace',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          maxWidth: 300,
                        }}>
                        {evt.input}
                      </Typography>
                    )}
                    {evt.phase === 'end' && evt.output && (
                      <Typography variant="caption" fontSize="0.55rem"
                        color={evt.is_error ? 'error.main' : 'text.secondary'}
                        sx={{
                          display: 'block', fontFamily: 'monospace',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          maxWidth: 300,
                        }}>
                        {evt.output}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Investigation Findings Card */}
      {findings && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          {/* Root Causes */}
          {findings.root_causes.length > 0 && (
            <Box sx={{ mb: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                <RootCauseIcon sx={{ fontSize: '0.75rem', color: 'error.main' }} />
                <Typography variant="caption" fontWeight={700} fontSize="0.65rem" color="error.main">
                  Root Causes
                </Typography>
              </Box>
              {findings.root_causes.map((cause, i) => (
                <Typography key={i} variant="caption" fontSize="0.65rem"
                  sx={{ display: 'block', pl: 1.5, py: 0.1 }}>
                  {cause}
                </Typography>
              ))}
            </Box>
          )}

          {/* Recommendations */}
          {findings.recommendations.length > 0 && (
            <Box sx={{ mb: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                <RecommendIcon sx={{ fontSize: '0.75rem', color: 'info.main' }} />
                <Typography variant="caption" fontWeight={700} fontSize="0.65rem" color="info.main">
                  Recommendations
                </Typography>
              </Box>
              {findings.recommendations.map((rec, i) => (
                <Typography key={i} variant="caption" fontSize="0.65rem"
                  sx={{ display: 'block', pl: 1.5, py: 0.1 }}>
                  {rec}
                </Typography>
              ))}
            </Box>
          )}

          {/* Summary */}
          {findings.summary && (
            <Alert severity="info" variant="outlined"
              sx={{
                py: 0, px: 0.75, fontSize: '0.65rem',
                '& .MuiAlert-message': { py: 0.25 },
              }}>
              {findings.summary}
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AgentActivityPanel;
